import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

const ARTEFACT_TYPE_OPTIONS = [
  'prd', 'sad', 'erd', 'sprint_plan', 'test_plan',
  'deployment_runbook', 'retro_report', 'user_stories',
] as const;

type ArtefactType = typeof ARTEFACT_TYPE_OPTIONS[number];

export interface ImportedArtefact {
  artefactId: string;
  filename: string;
  suggestedType: ArtefactType;
  confidence: number;
}

interface ImportResultsProps {
  results: ImportedArtefact[];
  projectId: string;
  onAnalyseComplete: (reportId: string) => void;
}

const CONFIDENCE_LABEL: Record<string, { label: string; class: string }> = {
  high:   { label: 'High',   class: 'bg-green-100 text-green-700' },
  medium: { label: 'Medium', class: 'bg-yellow-100 text-yellow-700' },
  low:    { label: 'Low',    class: 'bg-red-100 text-red-600' },
};

function confidenceTier(c: number): 'high' | 'medium' | 'low' {
  if (c >= 0.7) return 'high';
  if (c >= 0.5) return 'medium';
  return 'low';
}

export default function ImportResults({ results, projectId, onAnalyseComplete }: ImportResultsProps) {
  const navigate = useNavigate();
  const [types, setTypes] = useState<Record<string, ArtefactType>>(
    Object.fromEntries(results.map(r => [r.artefactId, r.suggestedType])),
  );
  const [analysing, setAnalysing] = useState(false);
  const [error, setError] = useState('');

  const handleTypeChange = async (artefactId: string, newType: ArtefactType) => {
    const previousType = types[artefactId];
    setTypes(prev => ({ ...prev, [artefactId]: newType }));
    try {
      await apiFetch(`/artefacts/${artefactId}/type`, {
        method: 'PATCH',
        body: JSON.stringify({ type: newType }),
      });
    } catch {
      // revert on failure
      setTypes(prev => ({ ...prev, [artefactId]: previousType }));
    }
  };

  const handleAnalyse = async () => {
    setAnalysing(true);
    setError('');
    try {
      const data = await apiFetch<{ reportId: string }>(`/projects/${projectId}/analyse`, {
        method: 'POST',
        body: JSON.stringify({ artefactIds: results.map(r => r.artefactId) }),
      });
      onAnalyseComplete(data.reportId);
      navigate(`/artefacts/${data.reportId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setAnalysing(false);
    }
  };

  if (analysing) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="animate-spin text-4xl">⚙️</div>
        <p className="text-gray-600 font-medium">Analysing your artefacts…</p>
        <p className="text-sm text-gray-400">This may take up to 60 seconds</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          {results.length} file{results.length !== 1 ? 's' : ''} imported
        </h3>
        <button
          onClick={handleAnalyse}
          disabled={results.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-[#1F3864] hover:bg-[#2E75B6] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Analyse Imported Artefacts
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Filename</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Detected Type</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Confidence</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map(r => {
              const tier = confidenceTier(r.confidence);
              const badge = CONFIDENCE_LABEL[tier];
              return (
                <tr key={r.artefactId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-800 font-medium">{r.filename}</td>
                  <td className="px-4 py-3">
                    <select
                      value={types[r.artefactId] ?? r.suggestedType}
                      onChange={e => handleTypeChange(r.artefactId, e.target.value as ArtefactType)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#1F3864]"
                    >
                      {ARTEFACT_TYPE_OPTIONS.map(t => (
                        <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
                      {badge.label} ({Math.round(r.confidence * 100)}%)
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
